import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import * as Sentry from '@sentry/react';
import { collectIntercomDiagnostics, consoleMessageForIntercomLoadCause, INTERCOM_HELP_CENTER_URL, makeBrowserDiagnosticsProbe, shouldReportIntercomLoadFailure, toScriptLoadError } from './intercomLoadDiagnostics';
import { makeSupportClickRecovery } from './intercomSupportClickRecovery';
export const INTERCOM_APP_ID = 'yx1cchhq';
const MAX_BOOT_ATTEMPTS = 3;
const BOOT_RETRY_DELAY_MS = 1000;
export const runWhenPageVisible = (run, page)=>{
    if (page.visibilityState === 'visible') {
        run();
        return;
    }
    const runOnceVisible = ()=>{
        if (page.visibilityState !== 'visible') {
            return;
        }
        page.removeEventListener('visibilitychange', runOnceVisible);
        run();
    };
    page.addEventListener('visibilitychange', runOnceVisible);
};
export const toWebBootData = (params)=>{
    const base = {
        app_id: INTERCOM_APP_ID,
        // eslint-disable-next-line no-restricted-syntax -- pure transport formatter outside React; no getNow seam here. Intercom boot created_at.
        created_at: Date.now(),
        hide_default_launcher: params.hideDefaultLauncher
    };
    if (params.jwt != null) {
        return _object_spread_props(_object_spread({}, base), {
            intercom_user_jwt: params.jwt
        });
    }
    return _object_spread_props(_object_spread({}, base), {
        email: params.email,
        user_id: params.userId,
        user_hash: params.userHash
    });
};
const isIntercomEnabledForEnvironment = ()=>window.enableIntercom && window.isProduction;
const toIntercomBootFailure = (message, diagnostics)=>Object.assign(new Error(message), {
        diagnostics
    });
const whenDocumentLoaded = (onLoaded)=>{
    if (document.readyState === 'complete') {
        onLoaded();
    } else if (window.attachEvent) {
        window.attachEvent('onload', onLoaded);
    } else {
        window.addEventListener('load', onLoaded, false);
    }
};
const loadScriptIntoDocument = (src)=>new Promise((resolve, reject)=>{
        whenDocumentLoaded(()=>{
            var _firstScript_parentNode;
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = src;
            script.onload = ()=>resolve();
            script.onerror = (error)=>reject(toScriptLoadError(error));
            const firstScript = document.getElementsByTagName('script')[0];
            (_firstScript_parentNode = firstScript.parentNode) === null || _firstScript_parentNode === void 0 ? void 0 : _firstScript_parentNode.insertBefore(script, firstScript);
        });
    });
const installIntercomQueueShim = ()=>{
    const queued = function queued1() {
        // eslint-disable-next-line prefer-rest-params
        queued.c(arguments);
    };
    queued.q = [];
    queued.c = function(args) {
        queued.q.push(args);
    };
    window.Intercom = queued;
};
const openHelpCenterInNewTab = ()=>void window.open(INTERCOM_HELP_CENTER_URL, '_blank');
const unknownDiagnosticsFor = (error, isOnline)=>({
        likelyCause: 'unknown',
        intercomFetchResult: 'unknown',
        thirdPartyFetchResult: 'unknown',
        cspViolations: [],
        isOnline,
        isPrivateBrowsing: null,
        scriptLoadError: {
            message: error instanceof Error ? error.message : String(error),
            type: 'error',
            raw: ''
        }
    });
export class WebIntercomClient {
    boot(params) {
        this.lastBootUserId = params.userId;
        const bootResult = this.bootWithRetry(toWebBootData(params), 1, []);
        void this.reportBootOutcome(bootResult, params.userId);
        return bootResult;
    }
    shutdown() {
        this.lastBootUserId = undefined;
        void this.getIntercom().then((intercom)=>intercom('shutdown'));
    }
    showNewMessage() {
        var _this_recovery;
        this.showNewMessageInWidget();
        (_this_recovery = this.recovery) === null || _this_recovery === void 0 ? void 0 : _this_recovery.observeClick();
    }
    update(settings) {
        void this.getIntercom().then((intercom)=>intercom('update', settings));
    }
    hideIntercom() {
        this.update({
            hide_default_launcher: true
        });
    }
    showIntercom() {
        this.update({
            hide_default_launcher: false
        });
    }
    isLoaded() {
        return this.loaded;
    }
    reportBootSkipped(reason, userId) {
        this.logBootOutcome("skipped_".concat(reason), {
            user_id: userId
        });
    }
    enableSupportClickDiagnostics(param) {
        let { requestHeal, canHeal, getUserId } = param;
        var // WHY: the recovery closure owns the heal cooldown and ladder latch — rebuilding it per re-arm resets both.
        _this_recovery;
        if (this.supportClickEnv == null) {
            return;
        }
        this.requestHeal = requestHeal;
        this.canHeal = canHeal;
        this.getUserIdFromStarter = getUserId;
        (_this_recovery = this.recovery) !== null && _this_recovery !== void 0 ? _this_recovery : this.recovery = makeSupportClickRecovery({
            env: this.supportClickEnv,
            requestHeal: ()=>{
                var _ref;
                var _this_requestHeal, _this;
                return (_ref = (_this_requestHeal = (_this = this).requestHeal) === null || _this_requestHeal === void 0 ? void 0 : _this_requestHeal.call(_this)) !== null && _ref !== void 0 ? _ref : Promise.resolve();
            },
            canHeal: ()=>{
                var _ref;
                var _this_canHeal, _this;
                return (_ref = (_this_canHeal = (_this = this).canHeal) === null || _this_canHeal === void 0 ? void 0 : _this_canHeal.call(_this)) !== null && _ref !== void 0 ? _ref : false;
            },
            reopenMessenger: ()=>this.showNewMessageInWidget(),
            isWidgetLoaded: ()=>this.isLoaded(),
            getUserId: ()=>{
                var _this_lastBootUserId;
                var _this_getUserIdFromStarter, _this;
                return (_this_lastBootUserId = this.lastBootUserId) !== null && _this_lastBootUserId !== void 0 ? _this_lastBootUserId : (_this_getUserIdFromStarter = (_this = this).getUserIdFromStarter) === null || _this_getUserIdFromStarter === void 0 ? void 0 : _this_getUserIdFromStarter.call(_this);
            }
        });
    }
    showNewMessageInWidget() {
        void this.getIntercom().then((intercom)=>{
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: 'showNewMessage called successfully',
                level: 'info'
            });
            intercom('showNewMessage');
        }).catch(()=>{
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: 'showNewMessage failed, redirecting to fallback',
                level: 'warning'
            });
            this.openHelpCenter();
        });
    }
    logBootOutcome(outcome, data) {
        var _this_logBIEvent, _this;
        if (!isIntercomEnabledForEnvironment()) {
            return;
        }
        (_this_logBIEvent = (_this = this).logBIEvent) === null || _this_logBIEvent === void 0 ? void 0 : _this_logBIEvent.call(_this, _object_spread({
            type: 'transaction',
            transaction_name: 'intercom_boot',
            status: 'end',
            outcome,
            page_name: 'intercom',
            hierarchy: [],
            pathname: window.location.pathname
        }, data));
    }
    reportBootOutcome(bootResult, userId) {
        return bootResult.then(()=>this.logBootOutcome('booted', {
                user_id: userId
            }), (error)=>{
            var _ref;
            const { diagnostics } = error;
            this.logBootOutcome('load_failed', {
                user_id: userId,
                failure_cause: (_ref = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.likelyCause) !== null && _ref !== void 0 ? _ref : 'unknown'
            });
        });
    }
    bootWithRetry(data, attempt, previousFailures) {
        return this.getIntercom({
            skipReporting: true
        }).then((intercom)=>{
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: "Intercom boot succeeded on attempt ".concat(attempt),
                level: 'info',
                data: {
                    attempt,
                    previousFailuresCount: previousFailures.length
                }
            });
            intercom('boot', data);
        }).catch((error)=>{
            var _error_diagnostics;
            const diagnostics = (_error_diagnostics = error.diagnostics) !== null && _error_diagnostics !== void 0 ? _error_diagnostics : unknownDiagnosticsFor(error, this.diagnosticsProbe.isOnline());
            const failure = {
                attempt,
                diagnostics,
                // eslint-disable-next-line no-restricted-syntax -- diagnostic timestamp for Sentry boot-failure reporting; not test-observable
                timestamp: new Date().toISOString()
            };
            const allFailures = [
                ...previousFailures,
                failure
            ];
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: "Intercom boot failed on attempt ".concat(attempt, "/").concat(MAX_BOOT_ATTEMPTS),
                level: 'warning',
                data: {
                    attempt,
                    likelyCause: diagnostics.likelyCause
                }
            });
            if (attempt < MAX_BOOT_ATTEMPTS) {
                return new Promise((resolve, reject)=>{
                    setTimeout(()=>{
                        this.bootWithRetry(data, attempt + 1, allFailures).then(resolve, reject);
                    }, this.retryDelayMs);
                });
            }
            this.reportLoadFailure(diagnostics, {
                attempts: allFailures,
                bootData: data
            });
            throw toIntercomBootFailure("Intercom boot failed after ".concat(MAX_BOOT_ATTEMPTS, " attempts"), diagnostics);
        });
    }
    reportLoadFailure(diagnostics, bootContext) {
        try {
            // eslint-disable-next-line no-console
            console.error(consoleMessageForIntercomLoadCause(diagnostics.likelyCause, diagnostics.isPrivateBrowsing));
            Sentry.withScope((scope)=>{
                var _diagnostics_isPrivateBrowsing;
                scope.setTag('intercom_likely_cause', diagnostics.likelyCause);
                scope.setTag('intercom_fetch_result', diagnostics.intercomFetchResult);
                scope.setTag('intercom_third_party_blocked', diagnostics.thirdPartyFetchResult.startsWith('failed') ? 'true' : 'false');
                scope.setTag('intercom_has_csp_violations', diagnostics.cspViolations.length > 0 ? 'true' : 'false');
                scope.setTag('intercom_private_browsing', String((_diagnostics_isPrivateBrowsing = diagnostics.isPrivateBrowsing) !== null && _diagnostics_isPrivateBrowsing !== void 0 ? _diagnostics_isPrivateBrowsing : 'unknown'));
                if (bootContext != null) {
                    scope.setTag('intercom_boot_attempts', String(bootContext.attempts.length));
                    scope.setContext('intercom_boot_failure', {
                        totalAttempts: bootContext.attempts.length,
                        bootDataKeys: Object.keys(bootContext.bootData),
                        attempts: bootContext.attempts.map((f)=>({
                                attempt: f.attempt,
                                timestamp: f.timestamp,
                                likelyCause: f.diagnostics.likelyCause,
                                intercomFetchResult: f.diagnostics.intercomFetchResult,
                                thirdPartyFetchResult: f.diagnostics.thirdPartyFetchResult,
                                isOnline: f.diagnostics.isOnline
                            }))
                    });
                    scope.setContext('intercom_final_diagnostics', _object_spread({}, diagnostics));
                    scope.setLevel('error');
                    Sentry.captureException(new Error("Intercom boot failed after ".concat(bootContext.attempts.length, " attempts: ").concat(diagnostics.likelyCause)));
                } else if (shouldReportIntercomLoadFailure(diagnostics.likelyCause, this.reportedSimpleLoadCauses)) {
                    this.reportedSimpleLoadCauses.add(diagnostics.likelyCause);
                    scope.setContext('intercom_diagnostics', _object_spread({}, diagnostics));
                    scope.setLevel('warning');
                    Sentry.captureException(new Error('failed to load intercom', {
                        cause: diagnostics.scriptLoadError
                    }));
                }
            });
        } catch (reportError) {
            Sentry.captureException(reportError, {
                tags: {
                    context: 'intercom_report_failure_failed'
                }
            });
        }
    }
    getIntercom(options) {
        if (!isIntercomEnabledForEnvironment()) {
            return Promise.resolve(()=>{});
        }
        if (this.failedToLoadIntercom) {
            this.failedToLoadIntercom = false;
            return this.loadIntercom(options);
        }
        if (this.loaded && window.Intercom) {
            return Promise.resolve(window.Intercom);
        }
        return this.loadIntercom(options);
    }
    loadIntercom(options) {
        if (document.visibilityState === 'visible') {
            return this.loadIntercomNow(options);
        }
        if (this.deferredVisibleLoad == null) {
            this.deferredVisibleLoad = new Promise((resolve, reject)=>{
                runWhenPageVisible(()=>{
                    this.deferredVisibleLoad = null;
                    this.loadIntercomNow(options).then(resolve, reject);
                }, document);
            });
        }
        return this.deferredVisibleLoad;
    }
    loadIntercomNow(options) {
        var _ref;
        window._intercom_app_id = INTERCOM_APP_ID;
        const skipReporting = (_ref = options === null || options === void 0 ? void 0 : options.skipReporting) !== null && _ref !== void 0 ? _ref : false;
        Sentry.addBreadcrumb({
            category: 'intercom',
            message: 'Attempting to load Intercom script',
            level: 'info'
        });
        const alreadyLoaded = window.Intercom;
        if (typeof alreadyLoaded === 'function' && this.loaded) {
            alreadyLoaded('reattach_activator');
            alreadyLoaded('update', window.intercomSettings);
            return Promise.resolve(window.Intercom);
        }
        installIntercomQueueShim();
        const scriptSrc = "https://widget.intercom.io/widget/".concat(INTERCOM_APP_ID);
        return this.loadScript(scriptSrc).then(()=>{
            this.loaded = true;
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: 'Intercom script loaded successfully',
                level: 'info'
            });
            return window.Intercom;
        }, (scriptLoadError)=>{
            this.failedToLoadIntercom = true;
            return collectIntercomDiagnostics(this.diagnosticsProbe, scriptSrc, scriptLoadError).then((diagnostics)=>{
                if (!skipReporting) {
                    this.reportLoadFailure(diagnostics);
                }
                throw toIntercomBootFailure("Failed to load Intercom: ".concat(diagnostics.likelyCause), diagnostics);
            });
        });
    }
    constructor({ logBIEvent, loadScript = loadScriptIntoDocument, retryDelayMs = BOOT_RETRY_DELAY_MS, diagnosticsProbe, openHelpCenter = openHelpCenterInNewTab, supportClickEnv } = {}){
        _define_property(this, "loaded", false);
        _define_property(this, "failedToLoadIntercom", false);
        _define_property(this, "reportedSimpleLoadCauses", new Set());
        _define_property(this, "deferredVisibleLoad", null);
        _define_property(this, "lastBootUserId", undefined);
        _define_property(this, "recovery", undefined);
        _define_property(this, "requestHeal", undefined);
        _define_property(this, "canHeal", undefined);
        _define_property(this, "getUserIdFromStarter", undefined);
        _define_property(this, "logBIEvent", void 0);
        _define_property(this, "loadScript", void 0);
        _define_property(this, "retryDelayMs", void 0);
        _define_property(this, "diagnosticsProbe", void 0);
        _define_property(this, "openHelpCenter", void 0);
        _define_property(this, "supportClickEnv", void 0);
        this.logBIEvent = logBIEvent;
        this.loadScript = loadScript;
        this.retryDelayMs = retryDelayMs;
        this.diagnosticsProbe = diagnosticsProbe !== null && diagnosticsProbe !== void 0 ? diagnosticsProbe : makeBrowserDiagnosticsProbe();
        this.openHelpCenter = openHelpCenter;
        this.supportClickEnv = supportClickEnv;
    }
}
